import { Vector3 } from '@babylonjs/core/Maths/math.vector';

/**
 * A view to capture from, in degrees from the scene's front (+Z, the viewer's front camera). Positive yaw moves the
 * eye towards -X (the scene's left as seen from the front), as ArcRotateCamera alpha grows; positive pitch raises it.
 */
export interface CaptureView { yaw: number; pitch: number }

/** A framed capture: the view to look from (absent: the editor camera's angles), and the PNG size whose aspect the content is framed to. */
export interface CaptureFrame {
    view?: CaptureView;
    width: number;
    height: number;
}

/** Share of the frame's band the content's box fills on its tighter side. */
const FRAMED_FILL = 0.9;

/**
 * ArcRotateCamera alpha/beta for a view. Yaw 0 looks from +Z, as the viewer's front camera does
 * (`alpha = PI/2`), and positive yaw moves the eye towards -X; beta stays clear of the poles, where the
 * orbit has no right axis.
 */
export function viewOrbit(view: CaptureView): { alpha: number; beta: number } {
    const rad = Math.PI / 180;
    return { alpha: Math.PI / 2 + view.yaw * rad, beta: Math.min(Math.PI - 0.01, Math.max(0.01, Math.PI / 2 - view.pitch * rad)) };
}

/**
 * Orbit distance at which every corner of the box falls inside the frame's band of the square render:
 * the full width for a wide frame (the full height for a tall one), with the box filling `FRAMED_FILL`.
 */
export function framedDistance(min: Vector3, max: Vector3, center: Vector3, alpha: number, beta: number, fov: number, aspect: number): number {
    const toEye = new Vector3(Math.cos(alpha) * Math.sin(beta), Math.cos(beta), Math.sin(alpha) * Math.sin(beta));
    const right = Vector3.Cross(Vector3.Up(), toEye).normalize();
    const up = Vector3.Cross(toEye, right).normalize();
    const tanX = Math.tan(fov / 2) * FRAMED_FILL * Math.min(1, aspect);
    const tanY = Math.tan(fov / 2) * FRAMED_FILL * Math.min(1, 1 / aspect);
    let distance = 0;
    for (const x of [min.x, max.x]) {
        for (const y of [min.y, max.y]) {
            for (const z of [min.z, max.z]) {
                const offset = new Vector3(x, y, z).subtractInPlace(center);
                // The corner sits at depth (distance - near) and must project inside the band on both axes.
                const near = Vector3.Dot(offset, toEye);
                distance = Math.max(distance, near + Math.abs(Vector3.Dot(offset, right)) / tanX, near + Math.abs(Vector3.Dot(offset, up)) / tanY);
            }
        }
    }
    return Math.max(distance, 0.01);
}

/**
 * `captureObjectThumbnail`'s framing options as a frame, or undefined for the square capture. Sizes are clamped
 * to 1..2048 px, and a non-finite one is replaced as `side` says; a view with a non-finite angle is dropped, so
 * the editor camera's angles are used. 
 */
export function captureFrameOf(opts: { size?: number; view?: CaptureView; width?: number; height?: number } | undefined, defaultSize: number): CaptureFrame | undefined {
    if (!opts || (opts.view === undefined && opts.width === undefined && opts.height === undefined)) return undefined;
    // A side that is missing or not a finite number takes `size`, and a `size` that is not one the default.
    const finite = (value: number | undefined): value is number => typeof value === 'number' && Number.isFinite(value);
    const side = (value: number | undefined): number =>
        Math.min(2048, Math.max(1, Math.round(finite(value) ? value : finite(opts.size) ? opts.size : defaultSize)));
    const view = opts.view && Number.isFinite(opts.view.yaw) && Number.isFinite(opts.view.pitch) ? { yaw: opts.view.yaw, pitch: opts.view.pitch } : undefined;
    return { ...(view ? { view } : {}), width: side(opts.width), height: side(opts.height) };
}

/** Crop the centred frame band while retaining RTT bottom-up row order. */
export function cropFramePixels(pixels: ArrayBufferView, renderSize: number, frame: CaptureFrame): { pixels: Uint8ClampedArray; width: number; height: number } {
    const src = new Uint8ClampedArray(pixels.buffer, pixels.byteOffset, pixels.byteLength);
    const aspect = frame.width / frame.height;
    const width = aspect >= 1 ? renderSize : Math.max(1, Math.round(renderSize * aspect));
    const height = aspect >= 1 ? Math.max(1, Math.round(renderSize / aspect)) : renderSize;
    const x0 = Math.floor((renderSize - width) / 2);
    const y0 = Math.floor((renderSize - height) / 2);
    const band = new Uint8ClampedArray(width * height * 4);
    for (let y = 0; y < height; y++) {
        const row = ((y0 + y) * renderSize + x0) * 4;
        band.set(src.subarray(row, row + width * 4), y * width * 4);
    }
    return { pixels: band, width, height };
}
